import React, {useState, useRef, useEffect} from 'react';
import {useSelector} from 'react-redux';
import * as C from '../../Common.styles';
import * as S from './LoginNew.styles';
import {maskEmail, maskMobile} from '../../../../../utils/utils';
import Eye from '../CreatePassword/EyeIcon';
import OTPBlock from '../../../UI/OTP/otp';
import {mobileCount, userCountrySelector, emailSelector} from '../../../../../store/selectors';
import {validations} from '../../../../../utils/validations';
import ButtonLoader from '../../../UI/Loader/Loader';
import {sendEvarData} from '@ibibo/analytics';
import {useAnalyticService} from '@ibibo/analytics-provider';
const LoginNew = ({
  userMobile,
  resendOTP,
  submitDetails,
  apiError,
  getToken,
  setLoader,
  sendOTP,
  updatePwdSuccessMessage,
  forgotPasswordLoader,
  is2FARequired,
}) => {
  const userCountry = useSelector(userCountrySelector);
  const [{country_code, username, password}, setUserPass] = useState({
    country_code: '+91',
    username: (!userCountry && userMobile) || '',
    password: '',
  });
  const {sendTrackingDataToSdk} = useAnalyticService();
  const [useOtp, setUseOtp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setError] = useState({});
  const mobileOtpRef = useRef(null);
  const mobile_count = useSelector(mobileCount);
  const email = useSelector(emailSelector);
  const [apiErrorMsg, setApiError] = useState(apiError);

  const mobileOtpLen = 6;
  const emailOtpLen = 6;

  useEffect(() => {
    setApiError(apiError);
  }, [apiError]);

  const fieldValidators = {
    country_code: {
      field: 'mobilecode',
      fieldName: 'Mobile Code',
      validators: ['required', 'validMobileCode'],
    },
    username: {
      field: userCountry ? '' : 'mobile',
      fieldName: 'Username',
      validators: ['required', 'userNameValue'],
    },
    password: {field: 'password', fieldName: 'Password', validators: ['required']},
  };

  const validateFields = data => {
    let errorObj = {};
    Object.keys(data).map(key => {
      if (fieldValidators[key]) {
        const validators = fieldValidators[key].validators;
        for (let i = 0; i < validators.length; i++) {
          const validateFunction = validations[validators[i]];
          const resp = validateFunction({
            value: data[key],
            fieldName: fieldValidators[key].fieldName,
            countryCode: userCountry ? '+971' : '+91',
            userCountry,
          });
          if (resp && !errorObj[key]) {
            errorObj[key] = resp;
          }
        }
      }
    });
    setError(errorObj);
    if (Object.keys(errorObj).length) {
      return true;
    }
    return false;
  };

  const maskField = (field, value) => {
    switch (field) {
      case 'email':
        return maskEmail(value);
      case 'mobile':
        return maskMobile(value);
    }
  };

  const onChangeHandler = (field, value) => {
    const data = {
      country_code,
      username,
      password,
    };
    data[field] = value;
    setUserPass(data);
  };

  let otpBlock = (
    <C.InputWrapper>
      <C.CommonInputWrap>
        <C.CommonLabel>Password</C.CommonLabel>
        <C.CommonInput
          type={!showPassword ? 'password' : 'text'}
          name="password"
          placeholder="Password"
          value={password}
          onChange={event => onChangeHandler('password', event.target.value)}
          hasError={errors['password'] ? true : false}
        />
        <C.ErrorText>{errors['password']}</C.ErrorText>
        <S.EyeWrapper>
          <Eye showPassword={showPassword} setShowPassword={() => setShowPassword(!showPassword)} />
        </S.EyeWrapper>
      </C.CommonInputWrap>
    </C.InputWrapper>
  );

  if (useOtp) {
    otpBlock = (
      <OTPBlock
        ref={mobileOtpRef}
        label=""
        subLabel=""
        otpLength={mobileOtpLen}
        type="motp"
        error={errors['mobile_otp']}
        resendOTP={resendOTP}
        autoFocus={true}
        resendVisible={mobile_count}
      />
    );
  }

  const getForgotPasswordToken = () => {
    const hasError = validateFields({username});
    if (!hasError) {
      getToken(username);
    }
  };

  const sendOTPFn = () => {
    const hasError = validateFields({
      country_code,
      username,
    });
    if (!hasError) {
      if (!useOtp) {
        setUseOtp(!useOtp);
        sendOTP({
          username: `${username}`,
        });
      } else {
        setUseOtp(!useOtp);
      }
    }
    sendEvarData(
      {
        prop54: !useOtp
          ? 'myPartner|login|password|loginWithOTP'
          : 'myPartner|login|OTP|loginWithPassword',
      },
      true,
    );
    sendTrackingDataToSdk?.(
      {
        event_detail: {
          event_name: 'click',
          event_type: 'user_action',
          event_value: 'Mobile Input Continue Click',
        },
      },
      'HOME',
    );
  };

  const submitLogin = event => {
    event.preventDefault();
    const hasError = validateFields({
      country_code,
      username,
      password: useOtp ? mobileOtpRef.current.value : password,
    });
    if (!hasError) {
      const dataObj = {
        username: `${username}`,
      };
      if (useOtp) {
        dataObj.mobile_otp = mobileOtpRef.current.value;
      } else {
        dataObj.password = password;
      }
      submitDetails(dataObj, sendOTPFn);
    }
    sendEvarData(
      {
        prop54: !useOtp ? 'myPartner|login|OTP|login' : 'myPartner|login|password|login',
      },
      true,
    );
  };

  return (
    <C.PopContentMainWrap>
      <C.PopContentHeadWrap>
        <C.PopContentHeadText>Login to your account</C.PopContentHeadText>
        {(!useOtp && (
          <C.PopContentSubHeadText>Enter Password to continue</C.PopContentSubHeadText>
        )) || (
          <C.PopContentSubHeadText>
            {is2FARequired
              ? 'Two-Factor Authentication required for this login.'
              : `OTP has been sent to your mobile number ${userMobile &&
                  maskField('mobile', userMobile)}`}
          </C.PopContentSubHeadText>
        )}
      </C.PopContentHeadWrap>
      {apiErrorMsg ? (
        <C.HeadSubTitleText>
          <C.HeadSubTitleTextSpan>{apiErrorMsg}</C.HeadSubTitleTextSpan>
        </C.HeadSubTitleText>
      ) : null}
      {updatePwdSuccessMessage ? (
        <C.HeadSubTitleText>
          <C.HeadSubTitleTextSpan>{updatePwdSuccessMessage}</C.HeadSubTitleTextSpan>
        </C.HeadSubTitleText>
      ) : null}
      <C.UserInfoForm onSubmit={submitLogin}>
        <C.PopContentInnerWrap>
          <S.OtpBlockWrap>
            <C.InputWrapper>
              <C.CommonInputWrap>
                <C.CommonLabel>{userCountry ? 'Email' : 'Mobile Number'}</C.CommonLabel>
                <C.CommonInput
                  type="text"
                  name="username"
                  value={username}
                  placeholder={userCountry ? 'Username (email)' : 'Username (mobile number)'}
                  onChange={event => onChangeHandler('username', event.target.value)}
                  hasError={errors['username'] ? true : false}
                />
                <C.ErrorText>{errors['username']}</C.ErrorText>
              </C.CommonInputWrap>
            </C.InputWrapper>
            <S.OtpMainWrap>{otpBlock}</S.OtpMainWrap>
          </S.OtpBlockWrap>
          {!useOtp ? (
            forgotPasswordLoader ? (
              <S.ForgotPasswordLoaderContainer>
                <ButtonLoader loaderType="gstin" />
              </S.ForgotPasswordLoaderContainer>
            ) : (
              <>
                {
                  <S.ForgotPasswordBlock>
                    <S.ForgotPasswordBlockText onClick={() => getForgotPasswordToken()}>
                      Forgot Password?
                    </S.ForgotPasswordBlockText>
                  </S.ForgotPasswordBlock>
                }
              </>
            )
          ) : null}
          {is2FARequired ? null : (
            <S.PasswordBlockWrap>
              <S.PasswordClickWrap onClick={sendOTPFn}>
                <S.PasswordText>{useOtp ? 'Login with password' : 'Login with OTP'}</S.PasswordText>
              </S.PasswordClickWrap>
            </S.PasswordBlockWrap>
          )}
          <C.ButtonWrap>
            <C.ContinueButton type="submit">
              {setLoader ? <ButtonLoader loaderType="button" /> : 'Login'}
            </C.ContinueButton>
          </C.ButtonWrap>
        </C.PopContentInnerWrap>
      </C.UserInfoForm>
    </C.PopContentMainWrap>
  );
};

export default LoginNew;
